import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Row,
  Col,
  Collapse,
  Card,
  CardBody,
  Table,
  Carousel,
  CarouselItem,
  CarouselControl,
  CarouselIndicators,
  CarouselCaption,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { SERVER_URL } from '../../../ServerLink';
import { Spinner } from 'react-bootstrap';

const MediaViewerModal = ({ isOpen, toggle, mediaUrl, mediaType, title }) => (
  <div className={`modal ${isOpen ? 'd-block' : 'd-none'}`} style={{ backgroundColor: 'rgba(0,0,0,0.9)' }} tabIndex="-1">
    <div className="modal-dialog modal-dialog-centered modal-xl">
      <div className="modal-content bg-dark text-white border-0">
        <div className="modal-header border-0 pb-2">
          <h5 className="modal-title">{title || 'Media Preview'}</h5>
          <button type="button" className="btn-close btn-close-white" onClick={toggle}></button>
        </div>
        <div className="modal-body text-center py-5">
          {mediaType?.startsWith('video/') ? (
            <video
              controls
              style={{
                maxHeight: '85vh',
                maxWidth: '100%',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
              }}
            >
              <source src={mediaUrl} type={mediaType} />
              Your browser does not support the video tag.
            </video>
          ) : (
            <img
              src={mediaUrl}
              alt={title}
              style={{
                maxHeight: '85vh',
                maxWidth: '100%',
                objectFit: 'contain',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
              }}
            />
          )}
        </div>
        <div className="modal-footer border-0 justify-content-center">
          <Button color="secondary" onClick={toggle}>Close</Button>
        </div>
      </div>
    </div>
  </div>
);

const SuiteInspectionDetailRow = ({ label, value, badgeColor = 'secondary', badgeText }) => (
  <tr className="align-middle">
    <td className="fw-semibold px-4 py-3" style={{ fontSize: '0.9rem' }}>
      <div className="d-flex align-items-center">
        <FeatherIcon icon="check-square" size={16} className="me-2 text-muted" />
        {label}
      </div>
    </td>
    <td className="text-center px-3 py-3">
      {badgeText ? (
        <span className={`badge bg-${badgeColor}`}>{badgeText}</span>
      ) : (
        value || '—'
      )}
    </td>
  </tr>
);

const SuiteInspectionUI = ({ inspectionData }) => {
  const { token } = useParams();
  const [selectedMedia, setSelectedMedia] = useState({ url: '', title: '', type: '' });
  const [openSections, setOpenSections] = useState([0, 1]); // open by default
  const [signedUrlCache, setSignedUrlCache] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  const toggleSection = (idx) => {
    setOpenSections((prev) =>
      prev.includes(idx) ? prev.filter((s) => s !== idx) : [...prev, idx]
    );
  };

  const getMediaKey = useCallback((attachment) => {
    if (typeof attachment !== 'string') return null;
    try {
      const url = new URL(attachment);
      return url.pathname.replace(/^\/+/, '');
    } catch {
      return attachment;
    }
  }, []);

  const getMultipleSignedUrls = async (keys) => {
    if (!Array.isArray(keys) || keys.length === 0) return {};

    try {
      const response = await fetch(`${SERVER_URL}/v2/suite-inspection-checklists/public/signed-urls`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ keys }),
      });

      if (!response.ok) throw new Error('Failed to generate signed URLs');

      const result = await response.json();
      return result.data || {};
    } catch (error) {
      console.error('Signed URLs fetch failed:', error);
      return {};
    }
  };

  useEffect(() => {
    setSignedUrlCache({});

    const attachments = inspectionData?.attachments || [];
    const keys = attachments.map(getMediaKey).filter(Boolean);

    if (keys.length > 0) {
      getMultipleSignedUrls([...new Set(keys)])
        .then((urlMap) => setSignedUrlCache(urlMap))
        .catch(console.error);
    }
  }, [inspectionData, token, getMediaKey]);

  // Carousel navigation
  const next = () => {
    if (animating) return;
    const nextIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(nextIndex);
  };

  const previous = () => {
    if (animating) return;
    const nextIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
    setActiveIndex(nextIndex);
  };

  const goToIndex = (newIndex) => {
    if (animating) return;
    setActiveIndex(newIndex);
  };

  // Prepare carousel items – numbered instead of filenames
  const attachments = inspectionData?.attachments || [];
  const items = attachments.map((att, index) => {
    const key = getMediaKey(att);
    const url = signedUrlCache[key] || 'https://via.placeholder.com/800x500?text=Loading...';
    const isVideo = /\.(mp4|mov|webm|avi|mkv)$/i.test(att);

    return {
      src: url,
      altText: `Media ${index + 1}`,
      caption: `Media ${index + 1}${isVideo ? ' (Video)' : ''}`,
      type: isVideo ? 'video' : 'image',
      index,
    };
  });

  const property = inspectionData?.property_details?.property_id || {};
  const inspectedBy = inspectionData?.inspectedByName || 'Unknown';

  const inspectionDate = inspectionData?.inspection_date
    ? new Date(inspectionData.inspection_date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    })
    : 'Not provided';

  const isApartment = inspectionData?.type === 'apartment';

  const getYesNoBadge = (value) => {
    if (value === undefined || value === null) return '—';
    const lower = String(value).toLowerCase().trim();
    if (['yes', 'true', '1', 'y'].includes(lower)) return 'Yes';
    if (['no', 'false', '0', 'n'].includes(lower)) return 'No';
    return value;
  };

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-4 py-3"
        style={{ backgroundColor: '#0D568F', minHeight: '64px' }}
      >
        <h5 className="mb-0 text-white fw-normal d-flex align-items-center">
          <FeatherIcon icon="eye" size={22} className="me-2" />
          Suite Inspection Checklist (Preview)
        </h5>
        <img src="/images/rentdigicare-logo.svg" alt="logo" style={{ height: '34px' }} />
      </div>

      <div className="container-fluid py-4" style={{ backgroundColor: '#f8f9fa' }}>
        {/* Property & Inspection Info */}
        <Card className="shadow-sm border-0 mb-4">
          <CardBody>
            <div className="text-primary mb-3 d-flex align-items-center fw-semibold">
              <FeatherIcon icon="home" size={17} strokeWidth={2.5} className="mb-1 me-2" />
              Property & Inspection Information
            </div>
            <Row className="g-4">
              <Col md={6}>
                <strong>Property Title:</strong>
                <br />
                {property.title || 'Not provided'}
              </Col>
              <Col md={6}>
                <strong>Address / Building:</strong>
                <br />
                {property.address || inspectionData?.property_details?.building_name || 'Not provided'}
              </Col>
              <Col md={6}>
                <strong>Unit Number:</strong>
                <br />
                {inspectionData?.unit_number || 'Not provided'}
              </Col>
              <Col md={6}>
                <strong>Inspection Date:</strong>
                <br />
                {inspectionDate}
              </Col>
              <Col md={6}>
                <strong>Inspected By:</strong>
                <br />
                {inspectedBy}
              </Col> 
              <Col md={6}>
                <strong>Suite Type:</strong>
                <br />
                {inspectionData?.type
                  ? inspectionData.type.charAt(0).toUpperCase() + inspectionData.type.slice(1)
                  : 'Not provided'}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Core Inspection Details */}
        <Card className="shadow-sm border-0 mb-4">
          <CardBody>
            <div className="text-primary mb-3 d-flex align-items-center fw-semibold">
              <FeatherIcon icon="check-square" size={17} strokeWidth={2.5} className="mb-1 me-2" />
              Inspection Results
            </div>

            <Table responsive hover className="table-bordered mb-0">
              <thead className="bg-light border-bottom border-2">
                <tr>
                  <th className="text-muted fw-semibold text-uppercase text-start">Field</th>
                  <th className="text-muted fw-semibold text-uppercase text-center">Value</th>
                </tr>
              </thead>
              <tbody>
                <SuiteInspectionDetailRow label="Cleanliness" value={inspectionData?.cleanliness} />
                <SuiteInspectionDetailRow
                  label="Any Damages"
                  badgeColor={inspectionData?.any_damages ? 'danger' : 'success'}
                  badgeText={inspectionData?.any_damages ? 'Yes' : 'No'}
                />
                <SuiteInspectionDetailRow label="Pets" value={inspectionData?.pets} />
                <SuiteInspectionDetailRow label="Plumbing" value={inspectionData?.plumbing} />
                <SuiteInspectionDetailRow label="Dye Test" value={inspectionData?.dye_test} />
                <SuiteInspectionDetailRow label="Dead Bolt Lubricated" value={inspectionData?.lubricate_dead_bolt} />
                <SuiteInspectionDetailRow label="Number of People" value={inspectionData?.number_of_people} />
                <SuiteInspectionDetailRow
                  label="Signs of Smoking"
                  badgeColor={inspectionData?.signs_of_smoking ? 'warning' : 'success'}
                  badgeText={inspectionData?.signs_of_smoking ? 'Yes' : 'No'}
                />
                <SuiteInspectionDetailRow
                  label="Overall Status"
                  badgeColor={inspectionData?.overall_status === 'passed' ? 'success' : 'danger'}
                  badgeText={
                    inspectionData?.overall_status === 'passed'
                      ? 'Passed'
                      : inspectionData?.overall_status === 'has issues'
                        ? 'Has Issues'
                        : '—'
                  }
                />
              </tbody>
            </Table>
          </CardBody>
        </Card>

        {/* Type-Specific Details */}
        {(isApartment || inspectionData?.type === 'townhouse') && (
          <Card className="shadow-sm border-0 mb-4">
            <CardBody>
              <div className="text-primary mb-3 d-flex align-items-center fw-semibold">
                <FeatherIcon icon="settings" size={17} strokeWidth={2.5} className="mb-1 me-2" />
                {isApartment ? 'Apartment-Specific Checks' : 'Townhouse-Specific Checks'}
              </div>

              <Table responsive hover className="table-bordered mb-0">
                <thead className="bg-light border-bottom border-2">
                  <tr>
                    <th className="text-muted fw-semibold text-uppercase text-start">Check</th>
                    <th className="text-muted fw-semibold text-uppercase text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {isApartment ? (
                    <>
                      <SuiteInspectionDetailRow
                        label="Closed Windows (Winters)"
                        badgeColor={
                          inspectionData?.closed_window_winters === 'no' ? 'danger' : 'success'
                        }
                        badgeText={getYesNoBadge(inspectionData?.closed_window_winters)}
                      />
                      <SuiteInspectionDetailRow
                        label="Furniture Away from Heating Pipes"
                        badgeColor={
                          inspectionData?.furniture_away_heating_pipes === 'no' ? 'danger' : 'success'
                        }
                        badgeText={getYesNoBadge(inspectionData?.furniture_away_heating_pipes)}
                      />
                    </>
                  ) : (
                    <>
                      <SuiteInspectionDetailRow
                        label="HRV Setting"
                        value={inspectionData?.hrv_setting}
                      />
                      <SuiteInspectionDetailRow
                        label="Furnace Filter"
                        value={inspectionData?.furnace_filter}
                      />
                      <SuiteInspectionDetailRow
                        label="Flooring Damage"
                        badgeColor={inspectionData?.flooring_damage ? 'danger' : 'success'}
                        badgeText={inspectionData?.flooring_damage ? 'Yes' : 'No'}
                      />
                    </>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        )}

        {/* Comments */}
        <Card className="shadow-sm border-0 mb-4">
          <CardBody>
            <div className="text-primary mb-3 d-flex align-items-center fw-semibold">
              <FeatherIcon icon="message-circle" size={17} strokeWidth={2.5} className="mb-1 me-2" />
              Comments
            </div>
            <p className="mb-0">
              {inspectionData?.comments || 'No additional comments provided.'}
            </p>
          </CardBody>
        </Card>

        {/* Attachments - Carousel with numbered labels */}
        {inspectionData?.attachments?.length > 0 && (
          <Card className="shadow-sm border-0 mb-5">
            <CardBody className="p-4 p-md-5">
              <div className="text-primary mb-4 d-flex align-items-center fw-semibold">
                <FeatherIcon icon="image" size={17} strokeWidth={2.5} className="mb-1 me-2" />
                Supporting Media ({inspectionData.attachments.length})
              </div>

              {items.length > 0 && (
                <>
                  <Carousel
                    activeIndex={activeIndex}
                    next={next}
                    previous={previous}
                    pause={false}
                    interval={false}
                    className="shadow rounded overflow-hidden mb-4"
                  >
                    <CarouselIndicators
                      items={items}
                      activeIndex={activeIndex}
                      onClickHandler={goToIndex}
                    />

                    {items.map((item) => (
                      <CarouselItem
                        onExiting={() => setAnimating(true)}
                        onExited={() => setAnimating(false)}
                        key={item.src}
                      >
                        {item.type === 'video' ? (
                          <div className="bg-dark text-center">
                            <video
                              controls
                              style={{
                                width: '100%',
                                maxHeight: '65vh',
                                objectFit: 'contain',
                              }}
                            >
                              <source src={item.src} type="video/mp4" />
                              Your browser does not support video.
                            </video>
                          </div>
                        ) : (
                          <img
                            src={item.src}
                            alt={item.altText}
                            style={{
                              width: '100%',
                              maxHeight: '65vh',
                              objectFit: 'contain',
                              background: '#111',
                            }}
                          />
                        )}

                        <CarouselCaption
                          captionText={item.caption}
                          className="bg-dark bg-opacity-75 text-white p-3"
                        />
                      </CarouselItem>
                    ))}

                    <CarouselControl direction="prev" directionText="Previous" onClickHandler={previous} />
                    <CarouselControl direction="next" directionText="Next" onClickHandler={next} />
                  </Carousel>

                  {/* Numbered Thumbnails */}
                  {items.length > 1 && (
                    <div className="d-flex flex-wrap justify-content-center gap-2">
                      {items.map((item, idx) => (
                        <div
                          key={idx}
                          className={`position-relative cursor-pointer rounded overflow-hidden border ${activeIndex === idx ? 'border-primary border-3 shadow' : 'border-light'
                            }`}
                          style={{ width: '90px', height: '70px' }}
                          onClick={() => goToIndex(idx)}
                        >
                          <img
                            src={item.src}
                            alt={item.altText}
                            className="w-100 h-100 object-cover"
                            style={{ opacity: activeIndex === idx ? 1 : 0.65 }}
                          />
                          <div
                            className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-70 text-white text-center py-1"
                            style={{ fontSize: '0.75rem' }}
                          >
                            {item.caption}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {!signedUrlCache[Object.keys(signedUrlCache)[0]] && items.length > 0 && (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Loading media...</p>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </div>

      {selectedMedia?.url && (
        <MediaViewerModal
          isOpen={!!selectedMedia.url}
          toggle={() => setSelectedMedia({ url: '', title: '', type: '' })}
          mediaUrl={selectedMedia.url}
          mediaType={selectedMedia.type}
          title={selectedMedia.title}
        />
      )}
    </>
  );
};

const PreviewSuiteInspection = () => {
  const { token } = useParams();
  const [inspectionData, setInspectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInspection = async () => {
      try {
        const response = await fetch(`${SERVER_URL}/v2/suite-inspection-checklists/public`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            response.status === 401 || response.status === 403
              ? 'Invalid or expired preview link'
              : 'Failed to load inspection'
          );
        }

        const result = await response.json();
        setInspectionData(result.data);
      } catch (err) {
        console.error('Preview fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInspection();
  }, [token]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !inspectionData) {
    return (
      <div className="container py-5 text-center">
        <h4 className="text-danger">Error</h4>
        <p>{error || 'Invalid or expired preview link'}</p>
        <Button color="primary" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  return <SuiteInspectionUI inspectionData={inspectionData} />;
};

export { SuiteInspectionUI, PreviewSuiteInspection };